import { Component, OnInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { filter, Subject, takeUntil } from 'rxjs';
import { ModuleList, softwareVersion } from 'src/app/constants/application.constant';
import { CommonCommunicationService } from 'src/app/core/services/commonCommunication.service';
import { UserService } from 'src/app/core/services/user.service';
import { safeWindowOpen } from 'src/app/core/utils/url-validator';

const HOVER_LEAVE_DELAY_MS = 300;
const SHOP_CHANGE_RELOAD_DELAY_MS = 500;
const TEAM_MEMBER_PAGE_SIZE = 100;
const PAGE_SIZE_INCREMENT = 5;
const INITIAL_PAGE_SIZE = 10;
const CARD_OFFSET_SPACING = 10;
const CARD_TOP_OFFSET = 8;
const CARD_LEFT_OFFSET = 8;
const CARD_TOP_ADJUSTMENT = 15;

interface SubItem {
  key: string;
  label: string;
  displayLabel: string;
  link: string;
}

interface SidebarModule {
  key: string;
  label: string;
  displayLabel: string;
  icon: string;
  activeIcon: string;
  link?: string;
  url?: string;
  isNew?: boolean;
  bypassPermission?: boolean;
  subItems: SubItem[];
}

interface RouteModuleMapping {
  path: string;
  module: string;
  subItemIndex: number;
  condition?: (url: string) => boolean;
  matchPaths?: string[];
}

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  standalone: false
})
export class SidebarComponent implements OnInit, OnDestroy {
  isDropDownLoading = false;
  currentActivePath = '';
  activeTabLabel = 'Dashboard';
  selectedModule: SidebarModule | null = null;

  userDetails: any = {};
  shopList: any[] = [];
  activeShopId = '';

  moduleList: SidebarModule[] = [];
  softwareVersion = '';
  isSidebarCollapsed = false;
  isGlassmorphicEnabled = false;
  hoveredModule: SidebarModule | null = null;
  subItemsCardPosition = { top: '0px', left: '0px' };
  isCardHovered = false;
  isHovered = false;
  totalItems = 0;
  currentPageSize = INITIAL_PAGE_SIZE;

  private readonly allModuleList: any[] = ModuleList;
  private rolePermission: any;
  private shopIdsList: string[] = [];
  private hoverLeaveTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly destroy$ = new Subject<void>();

  /**
   * Pre-built lookup map: path → RouteModuleMapping[]
   * O(1) path lookup instead of O(n) array scan on every navigation.
   * A path may map to multiple entries — resolved by each entry's optional condition check.
   */
  private readonly routeMappings: RouteModuleMapping[] = [
    { path: '/offers/add-offer', module: 'Offers', subItemIndex: 0 },
    { path: '/offers/promotions/add-edit-promotion', module: 'Offers', subItemIndex: 1 },
    { path: '/product/add-collection', module: 'Product Listing', subItemIndex: 0 },
    { path: '/product/add-product', module: 'Product Listing', subItemIndex: 1 },
    { path: '/product/add-bundle', module: 'Product Listing', subItemIndex: 2 },
    { path: '/product/bundles/add', module: 'Product Listing', subItemIndex: 2, matchPaths: ['/product/bundles/edit'] },
    { path: '/product/inventory-ledger', module: 'Product Listing', subItemIndex: 4 },
    { path: '/orders/order-details', module: 'Orders', subItemIndex: 0 },
    { path: '/customers/add-customer', module: 'Customers', subItemIndex: 0 },
    { path: '/customers/add-customer-group', module: 'Customers', subItemIndex: 1 },
    { path: '/cms/edit-content', module: 'Website', subItemIndex: 1, matchPaths: ['/cms/add-content'] },
    { path: '/appearance/add-blog', module: 'Website', subItemIndex: 3 },
    { path: '/appearance/add-page', module: 'Website', subItemIndex: 4 },
    { path: '/appearance/add-announcement-bar', module: 'Website', subItemIndex: 5 },
    { path: '/marketing/new-campaign', module: 'Marketing', subItemIndex: 1 },
    { path: '/marketing/referrals/add-edit-referral', module: 'Marketing', subItemIndex: 2 },
    { path: '/plugins/view', module: 'Plugins', subItemIndex: 0 },
    { path: '/plugins/add', module: 'Plugins', subItemIndex: 0 },
    { path: '/plugins/settings', module: 'Plugins', subItemIndex: 0 },
    {
      // Connected apps: the tab is a query param (?tab=installed) and the set-up steps are
      // child paths, so none of them is an exact sub-item link.
      path: '/plugins/connected-apps',
      module: 'Plugins',
      subItemIndex: 1,
      matchPaths: ['/plugins/connected-apps/m2b/review', '/plugins/connected-apps/m2b/code']
    },
    {
      path: '/finance/remittance-summary',
      module: 'Finances',
      subItemIndex: 0,
      matchPaths: ['/finance/remittance-summary/order-details']
    }
  ];

  private readonly routeLookup = this.buildRouteLookup();

  private buildRouteLookup(): Map<string, RouteModuleMapping[]> {
    const map = new Map<string, RouteModuleMapping[]>();

    for (const entry of this.routeMappings) {
      // Index by primary path
      const existing = map.get(entry.path) || [];
      existing.push(entry);
      map.set(entry.path, existing);

      // Index by alternate paths
      for (const alt of entry.matchPaths || []) {
        const altExisting = map.get(alt) || [];
        altExisting.push(entry);
        map.set(alt, altExisting);
      }
    }

    return map;
  }

  constructor(
    private router: Router,
    private ccs: CommonCommunicationService,
    private userService: UserService,
    private changeDetectorRef: ChangeDetectorRef,
    private translatePipe: TranslatePipe
  ) {
    this.subscribeToSidebarState();
    this.subscribeToRouterEvents();
  }

  ngOnInit(): void {
    this.softwareVersion = softwareVersion;
    this.userDetails = this.ccs.userData || {};
    this.translateSidebarItems(this.allModuleList);
    this.subscribeToActiveShopId();
    this.subscribeToPermissions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearHoverTimeout();
  }

  // ────────────────────────────────────────────
  // Public API (used by template)
  // ────────────────────────────────────────────

  toggleSidebar(): void {
    this.ccs.toggleSidebar();
  }

  changeShop(shopId: string): void {
    this.userService
      .switchShop(shopId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.success && res?.token) {
            localStorage.setItem('token', JSON.stringify(res.token));
          }
          // Adopt the rotated refresh token for the newly-selected shop so a later
          // token refresh renews into this shop, not the previous one.
          if (res?.refreshToken) {
            localStorage.setItem('refreshToken', res.refreshToken);
          }
          localStorage.removeItem('isSetupComplete');
          localStorage.removeItem('planObj');
          localStorage.setItem('activeShopId', shopId);
          location.reload();
        },
        error: () => {
          localStorage.removeItem('isSetupComplete');
          localStorage.removeItem('planObj');
          localStorage.setItem('activeShopId', shopId);
          location.reload();
        }
      });
  }

  navigateTo(data: any, type: string, module?: SidebarModule): void {
    if (!this.ccs.isSetupComplete.value && data.key === 'dashboard') {
      data.link = '/onboarding';
    } else if (this.ccs.isSetupComplete.value && data.key === 'dashboard') {
      data.link = '/dashboard';
    }

    const navigated = type === 'item' ? this.handleModuleClick(data, module) : this.handleSubItemClick(data, module!);

    if (!navigated) {
      this.syncSidebarToCurrentRoute();
    }

    this.changeDetectorRef.detectChanges();
    this.hoveredModule = null;
    this.isHovered = false;
  }

  onHover(module: SidebarModule, event: MouseEvent): void {
    if (!this.isSidebarCollapsed) return;

    this.hoveredModule = module;
    this.clearHoverTimeout();

    const rect = (event.target as HTMLElement).getBoundingClientRect();
    this.subItemsCardPosition = {
      top: `${rect.top - CARD_TOP_OFFSET}px`,
      left: `${rect.right + CARD_LEFT_OFFSET}px`
    };

    requestAnimationFrame(() => this.adjustCardPosition(rect, window.innerHeight));
  }

  onLeave(): void {
    this.scheduleCardHide();
  }

  onCardHover(): void {
    this.isCardHovered = true;
    this.clearHoverTimeout();
  }

  onCardLeave(): void {
    this.isCardHovered = false;
    this.scheduleCardHide();
  }

  onScrollToEnd(_event: any): void {
    if (this.totalItems !== this.shopList.length) {
      this.currentPageSize += PAGE_SIZE_INCREMENT;
      this.isDropDownLoading = true;
      this.fetchShopList();
    }
  }

  navigateToRoute(): void {
    this.router.navigateByUrl('/onboarding');
  }

  // ────────────────────────────────────────────
  // Subscriptions (initialized once)
  // ────────────────────────────────────────────

  private subscribeToSidebarState(): void {
    this.ccs.isSidebarCollapsed$
      .pipe(takeUntil(this.destroy$))
      .subscribe((collapsed) => (this.isSidebarCollapsed = collapsed));
  }

  private subscribeToRouterEvents(): void {
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.currentActivePath = this.router.url.split('?')[0] || '/';
        this.syncSidebarToCurrentRoute();
      });
  }

  private subscribeToActiveShopId(): void {
    this.ccs
      .getActiveShopId()
      .pipe(takeUntil(this.destroy$))
      .subscribe((shopId) => {
        if (shopId) {
          this.activeShopId = shopId;
          this.extractShopIds();
          this.fetchShopList();
        }
      });
  }

  private subscribeToPermissions(): void {
    this.ccs
      .getRolePermission()
      .pipe(takeUntil(this.destroy$))
      .subscribe((permissions) => {
        if (!permissions) return;

        this.rolePermission = permissions;
        this.ccs.setModuleList(permissions);

        const filtered = this.buildFilteredModules();
        const routeFound = this.activateModuleForCurrentRoute(filtered);
        this.moduleList = structuredClone(filtered);

        if (!routeFound) {
          this.syncSidebarToCurrentRoute();
        }
      });
  }

  // ────────────────────────────────────────────
  // Navigation helpers
  // ────────────────────────────────────────────

  private handleModuleClick(data: any, module?: SidebarModule): boolean {
    if (this.selectedModule?.label === data.label) {
      this.activeTabLabel = module?.label || data.label;
      this.selectedModule = module ? null : this.selectedModule;
      this.currentActivePath = '';
      return false;
    }

    this.activeTabLabel = data.label;
    this.selectedModule = data;

    const targetLink = data.link || data.subItems?.[0]?.link;
    if (targetLink) {
      this.currentActivePath = targetLink;
      this.router.navigateByUrl(targetLink);
      return true;
    }

    if (data.url) {
      safeWindowOpen(data.url, '_blank');
      return true;
    }

    this.currentActivePath = '';
    return true;
  }

  private handleSubItemClick(data: SubItem, module: SidebarModule): boolean {
    this.activeTabLabel = module.label;
    this.currentActivePath = data.link;
    this.selectedModule = module;
    this.router.navigateByUrl(data.link);
    return true;
  }

  private syncSidebarToCurrentRoute(): void {
    const currentPath = this.router.url.split('?')[0];
    const fullUrl = this.router.url;

    const candidates = this.routeLookup.get(currentPath);
    if (!candidates) return;

    const mapping = candidates.find((m) => !m.condition || m.condition(fullUrl));
    if (!mapping) return;

    const mod = this.moduleList.find((m) => m.label === mapping.module);
    if (mod) {
      this.activeTabLabel = mod.label;
      this.currentActivePath = mod.subItems[mapping.subItemIndex]?.link || '';
      this.selectedModule = mod;
    }
  }

  private activateModuleForCurrentRoute(modules: SidebarModule[]): boolean {
    const currentUrl = this.router.url;

    for (const mod of modules) {
      if (mod.subItems?.length) {
        const subItem = mod.subItems.find((s) => s.link === currentUrl);
        if (subItem) {
          this.activeTabLabel = mod.label;
          this.currentActivePath = subItem.link;
          this.selectedModule = mod;
          return true;
        }
      } else if (mod.link === currentUrl) {
        this.activeTabLabel = mod.label;
        this.currentActivePath = mod.link!;
        this.selectedModule = mod;
        return true;
      }
    }

    return false;
  }

  // ────────────────────────────────────────────
  // Permission filtering
  // ────────────────────────────────────────────

  private buildFilteredModules(): SidebarModule[] {
    return this.allModuleList.reduce<SidebarModule[]>((result, mod) => {
      if (mod.subItems?.length) {
        const filteredSubs = mod.subItems.filter((sub: SubItem) => this.rolePermission[sub.key]?.view === true);
        if (filteredSubs.length) {
          result.push({ ...mod, subItems: filteredSubs });
        }
      } else if (mod.bypassPermission || this.rolePermission[mod.key]?.view === true) {
        result.push(mod);
      }
      return result;
    }, []);
  }

  // ────────────────────────────────────────────
  // Shop management
  // ────────────────────────────────────────────

  private extractShopIds(): void {
    this.shopIdsList = (this.userDetails?.shops || []).filter((s: any) => s?.shop_id).map((s: any) => s.shop_id);
  }

  private fetchShopList(searchTerm = ''): void {
    this.userService
      .getShopListByIds({ shopIds: this.shopIdsList, searchText: searchTerm })
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        this.shopList = res.result;
      });
  }

  // ────────────────────────────────────────────
  // Hover card positioning
  // ────────────────────────────────────────────

  private adjustCardPosition(triggerRect: DOMRect, viewportHeight: number): void {
    const card = document.querySelector('.sub-items-card') as HTMLElement;
    if (!card) return;

    card.classList.add('visible');
    const cardBottom = card.getBoundingClientRect().bottom;

    if (cardBottom > viewportHeight) {
      const overflow = cardBottom - viewportHeight + CARD_OFFSET_SPACING;
      card.style.top = `${triggerRect.top - CARD_TOP_ADJUSTMENT - overflow}px`;
      card.classList.add('caret-up');
    } else {
      card.classList.remove('caret-up');
    }
  }

  private scheduleCardHide(): void {
    this.hoverLeaveTimeout = setTimeout(() => {
      if (!this.isCardHovered) {
        this.hoveredModule = null;
        const card = document.querySelector('.sub-items-card') as HTMLElement;
        card?.classList.remove('visible');
      }
    }, HOVER_LEAVE_DELAY_MS);
  }

  private clearHoverTimeout(): void {
    if (this.hoverLeaveTimeout) {
      clearTimeout(this.hoverLeaveTimeout);
      this.hoverLeaveTimeout = null;
    }
  }

  // ────────────────────────────────────────────
  // Translation
  // ────────────────────────────────────────────

  private translateSidebarItems(items: any[]): void {
    if (!items?.length) return;

    for (const item of items) {
      if (item.displayLabel) {
        item.displayLabel = this.translatePipe.transform(item.displayLabel);
      }
      for (const sub of item.subItems || []) {
        if (sub.displayLabel) {
          sub.displayLabel = this.translatePipe.transform(sub.displayLabel);
        }
      }
    }
  }
}
